import * as Sentry from "@sentry/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useWebSocket from "react-use-websocket";
import { toast } from "sonner";

import { buildProductionApiUrl } from "utils";
import { getUser, isAuthenticated } from "api/cookie_auth";
import { type AudioConfig, audioRecorder } from "screens/call/audio-recorder";

import type { SocketCuePayload } from "./use-cues";

const WEBSOCKET_RECONNECT_ATTEMPTS = 10;
const WEBSOCKET_RECONNECT_INTERVAL = 5000;
const WEBSOCKET_READY_STATE_OPEN = 1;
const WEBSOCKET_STATE_CONNECTING = 0;
const WEBSOCKET_STATE_OPEN = 1;
const WEBSOCKET_STATE_CLOSING = 2;
const WEBSOCKET_STATE_CLOSED = 3;
const WEBSOCKET_CLOSE_NORMAL = 1000;
const WEBSOCKET_CLOSE_GOING_AWAY = 1001;
const WEBSOCKET_CLOSE_NO_STATUS = 1005; // browser pseudo-code: clean close without explicit status frame

export type CallAudioState = {
  isRecording: boolean;
  isConnected: boolean;
  connectionState: "Closed" | "Connecting" | "Open" | "Closing";
  error: string | null;
};

export type SocketTranscriptPayload = {
  duration: number;
  is_interim_result: boolean;
  speaker: string;
  // 0 = agent, 1 = customer. Always 0 for mono streams.
  // Optional for backwards compatibility with backends that predate multichannel support.
  channel?: number;
  start: number;
  text: string;
};

export type UseCallAudioProps = {
  organisationId: number;
  channelId: number;
  audioConfig: AudioConfig;
  onTranscript?: (transcript: SocketTranscriptPayload) => void;
  onCue?: (cue: SocketCuePayload[]) => void;
  shouldConnect: boolean;
};

export const useCallAudio = ({
  organisationId,
  channelId,
  audioConfig,
  onTranscript,
  onCue,
  shouldConnect,
}: UseCallAudioProps) => {
  const [analyserNode, setAnalyserNode] = useState<AnalyserNode | null>(null);
  const [callAudioState, setCallAudioState] = useState<CallAudioState>({
    isRecording: false,
    isConnected: false,
    connectionState: "Closed",
    error: null,
  });

  const audioDataBuffer = useRef<ArrayBuffer[]>([]);
  const currentUser = getUser();
  const isAuth = isAuthenticated();

  const socketUrl = useMemo(() => {
    if (!(isAuth && currentUser && organisationId && channelId && shouldConnect)) {
      return null;
    }
    const baseUrl = import.meta.env.DEV ? "/ws" : buildProductionApiUrl("/ws", true);

    return `${baseUrl}/channel/${channelId}/stream/`;
  }, [isAuth, currentUser, organisationId, channelId, shouldConnect]);

  // Only tell Deepgram channels=2 when a system audio stream is actually present.
  // Without it the recorder sends mono regardless of the channels setting.
  const effectiveChannels = audioConfig.channels === 2 && audioConfig.systemAudioStream ? 2 : 1;

  const wsOptions = useMemo(
    () => ({
      queryParams: {
        channels: effectiveChannels.toString(),
        encoding: "linear16",
        sample_rate: audioConfig.sampleRate.toString(),
        interim_results: "true",
      },
      shouldReconnect: (closeEvent) => {
        if (!shouldConnect) return false;

        // don't reconnect on clean/intentional closes
        if (
          closeEvent.code === WEBSOCKET_CLOSE_NORMAL ||
          closeEvent.code === WEBSOCKET_CLOSE_GOING_AWAY ||
          closeEvent.code === WEBSOCKET_CLOSE_NO_STATUS
        )
          return false;

        return true; // abnormal close -> reconnect
      },
      reconnectAttempts: WEBSOCKET_RECONNECT_ATTEMPTS,
      reconnectInterval: WEBSOCKET_RECONNECT_INTERVAL,
      onOpen: () => {
        setCallAudioState((prev) => ({
          ...prev,
          isConnected: true,
          connectionState: "Open",
          error: null,
        }));
      },
      onClose: (event) => {
        setCallAudioState((prev) => ({
          ...prev,
          isConnected: false,
          connectionState: "Closed",
        }));
        console.info("WS close code:", event.code, "reason:", event.reason);
        if (
          event.code !== WEBSOCKET_CLOSE_NORMAL &&
          event.code !== WEBSOCKET_CLOSE_GOING_AWAY &&
          event.code !== WEBSOCKET_CLOSE_NO_STATUS
        ) {
          Sentry.captureMessage("WebSocket closed unexpectedly", {
            level: "warning",
            extra: { code: event.code, reason: event.reason, channelId, organisationId },
          });
        }
      },
      onError: () => {
        setCallAudioState((prev) => ({
          ...prev,
          isConnected: false,
          error: "WebSocket connection failed",
          connectionState: "Closed",
        }));
        Sentry.captureMessage("WebSocket connection error", {
          level: "error",
          extra: { channelId, organisationId },
        });
      },
      onReconnectStop: () => {
        Sentry.captureMessage("WebSocket reconnect attempts exhausted", {
          level: "error",
          extra: { channelId, organisationId },
        });
      },
      onMessage: (event) => {
        if (event.data) {
          try {
            const decoded = JSON.parse(event.data);
            if (decoded.type === "alerts") {
              onCue?.(decoded.payload as SocketCuePayload[]);
            } else if (decoded.type === "transcript") {
              const payload = decoded.payload as SocketTranscriptPayload;
              // Console helps with debugging
              console.info(payload);
              onTranscript?.(payload);
            }
          } catch (error) {
            console.error(error);
            Sentry.captureException(error);
          }
        }
      },
    }),
    [
      effectiveChannels,
      audioConfig.sampleRate,
      channelId,
      organisationId,
      onCue,
      onTranscript,
      shouldConnect,
    ],
  );

  const { sendMessage, readyState, getWebSocket } = useWebSocket(socketUrl, wsOptions);

  useEffect(() => {
    const stateMap: Record<number, CallAudioState["connectionState"]> = {
      [WEBSOCKET_STATE_CONNECTING]: "Connecting",
      [WEBSOCKET_STATE_OPEN]: "Open",
      [WEBSOCKET_STATE_CLOSING]: "Closing",
      [WEBSOCKET_STATE_CLOSED]: "Closed",
    };
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setCallAudioState((prev) => ({
      ...prev,
      connectionState: stateMap[readyState] || "Closed",
      isConnected: readyState === WEBSOCKET_STATE_OPEN,
    }));
  }, [readyState]);

  const startRecording = useCallback(async () => {
    if (!audioConfig.inputDevice) {
      toast.error("No audio device selected");
      return;
    }

    if (!isAuth) {
      toast.error("Authentication required to start recording");
      return;
    }

    try {
      setCallAudioState((prev) => ({ ...prev, error: null }));

      await audioRecorder.startRecording(audioConfig, (audioData) => {
        if (readyState === WEBSOCKET_READY_STATE_OPEN) {
          try {
            const uint8Array = new Uint8Array(audioData.audioBuffer);
            sendMessage(uint8Array.buffer);
          } catch (error) {
            console.error(error);
          }
        } else {
          audioDataBuffer.current.push(new Uint8Array(audioData.audioBuffer).buffer);
        }
      });

      setCallAudioState((prev) => ({
        ...prev,
        isRecording: true,
        error: null,
      }));
      setAnalyserNode(audioRecorder.analyser);

      if (readyState === WEBSOCKET_READY_STATE_OPEN && audioDataBuffer.current.length > 0) {
        for (const buffer of audioDataBuffer.current) {
          sendMessage(buffer);
        }
        audioDataBuffer.current = [];
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to start recording";
      setCallAudioState((prev) => ({ ...prev, error: errorMessage }));
      toast.error(errorMessage);
    }
  }, [audioConfig, readyState, sendMessage, isAuth]);

  const stopRecording = useCallback(() => {
    audioRecorder.stopRecording();
    audioDataBuffer.current = [];
    setAnalyserNode(null);
    setCallAudioState((prev) => ({ ...prev, isRecording: false }));
  }, []);

  const isReadyToRecord = useCallback(
    () => !!(audioConfig.inputDevice && isAuth && currentUser && !callAudioState.isRecording),
    [audioConfig.inputDevice, isAuth, currentUser, callAudioState.isRecording],
  );

  return {
    ...callAudioState,
    startRecording,
    stopRecording,
    isReadyToRecord: isReadyToRecord(),
    connectionState: callAudioState.connectionState,
    webSocketIsReady: readyState === WEBSOCKET_READY_STATE_OPEN,
    websocket: getWebSocket(),
    analyserNode,
  };
};
